import type { SVGProps } from 'react';

/**
 * Einfarbige Piktogramme für die Oberfläche.
 *
 * Gestaltungsregeln (siehe Plan):
 * - Zeichenfläche 24 x 24, Hauptkonturen mit Strichstärke 1,8, Details mit 1,4
 * - abgerundete Linienenden und Ecken
 * - ausschließlich currentColor, damit Hell- und Dunkelmodus von selbst passen
 */
export const iconNames = [
  'book',
  'search',
  'cart',
  'tag',
  'sliders',
  'hat',
  'clock',
  'star',
  'plus',
  'menu',
  'flame',
  'knife',
  'bowl',
  'check',
  'heart',
  'sun',
  'moon',
  'device',
  'barcode',
  'library',
  'grip',
  'image',
  'video',
] as const;

/** Name eines Piktogramms. */
export type IconName = (typeof iconNames)[number];

/** Strichstärken der Piktogramme. */
export const ICON_STROKE = {
  primary: 1.8,
  detail: 1.4,
} as const;

/** Pfade der Piktogramme, jeweils als SVG-Inhalt. */
const paths: Record<IconName, { primary: string; detail?: string }> = {
  book: {
    primary:
      'M12 6.5C10.3 5.2 8.3 4.6 6 4.6H3.8v13.2H6c2.3 0 4.3.6 6 1.9 1.7-1.3 3.7-1.9 6-1.9h2.2V4.6H18c-2.3 0-4.3.6-6 1.9Z',
    detail: 'M12 6.5v13.2',
  },
  search: {
    primary: 'M10.8 17.6a6.8 6.8 0 1 0 0-13.6 6.8 6.8 0 0 0 0 13.6Z',
    detail: 'M15.8 15.8 20.5 20.5',
  },
  cart: {
    primary:
      'M3 4.5h2.1l2.2 9.6h9.9l2.1-7H5.6M9.3 18.6a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0Zm8.6 0a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0Z',
  },
  tag: {
    primary: 'M4 4.9h7.4l8 8a1.8 1.8 0 0 1 0 2.5l-4 4a1.8 1.8 0 0 1-2.5 0l-8-8Z',
    detail: 'M8.3 8.3h.01',
  },
  sliders: {
    primary: 'M4 7.5h3.2M12.2 7.5H20M4 16.5h7.8M16.8 16.5H20',
    detail: 'M9.7 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm4.6 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  },
  hat: {
    primary:
      'M8.4 13.8a4.4 4.4 0 0 1-1.1-8.6 4.6 4.6 0 0 1 8.7-1.2 4.4 4.4 0 0 1 1.9 8.4M6.5 13.8h11M7.2 17.4h9.6',
    detail: 'M8.4 13.8v3.6m7.2-3.6v3.6',
  },
  clock: {
    primary: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
    detail: 'M12 7.2V12l3.4 2',
  },
  star: {
    primary: 'm12 3.6 2.6 5.5 5.9.8-4.3 4.2 1 6-5.2-2.9-5.2 2.9 1-6L3.5 9.9l5.9-.8Z',
  },
  plus: {
    primary: 'M12 5.5v13M5.5 12h13',
  },
  menu: {
    primary: 'M4 7h16M4 12h16M4 17h16',
  },
  flame: {
    primary: 'M12 3.5c3.6 3.4 5.4 6.1 5.4 8.6a5.4 5.4 0 0 1-10.8 0c0-1.4.6-2.6 1.7-3.9.9-1 2.1-2.6 3.7-4.7Z',
    detail: 'M12 18a2.4 2.4 0 0 0 2.4-2.4c0-1-.7-1.9-2.4-3.4-1.7 1.5-2.4 2.4-2.4 3.4A2.4 2.4 0 0 0 12 18Z',
  },
  knife: {
    primary: 'M4.6 19.4 17.8 6.2a2 2 0 0 1 2.8 2.8L7.4 22.2Z',
    detail: 'M9.4 14.6l2.8 2.8',
  },
  bowl: {
    primary: 'M3.6 11.6h16.8a8.4 8.4 0 0 1-16.8 0Zm2.8 8.8h11.2',
    detail: 'M9 8.4c0-1.6 1.3-2.9 3-2.9s3 1.3 3 2.9',
  },
  check: {
    primary: 'm5.5 12.5 4.2 4.2 8.8-9.4',
  },
  heart: {
    primary: 'M12 20.3 4.9 13.4A4.6 4.6 0 0 1 12 7.1a4.6 4.6 0 0 1 7.1 6.3Z',
  },
  sun: {
    primary: 'M12 16.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4Z',
    detail:
      'M12 2.8v1.9M12 19.3v1.9M2.8 12h1.9M19.3 12h1.9M5.5 5.5l1.3 1.3M17.2 17.2l1.3 1.3M5.5 18.5l1.3-1.3M17.2 6.8l1.3-1.3',
  },
  moon: {
    primary: 'M19.6 14.6A8 8 0 0 1 9.4 4.4a8 8 0 1 0 10.2 10.2Z',
  },
  device: {
    primary:
      'M5 4.8h14a1.2 1.2 0 0 1 1.2 1.2v9a1.2 1.2 0 0 1-1.2 1.2H5A1.2 1.2 0 0 1 3.8 15V6A1.2 1.2 0 0 1 5 4.8Z',
    detail: 'M9 20h6M12 16.2V20',
  },
  library: {
    primary: 'M4 4.5h4v15H4ZM10 4.5h4v15h-4Z',
    detail: 'M15.6 5.6l3.4-.9 2.9 14.4-3.4.9ZM4 8h4M10 8h4M4 16h4M10 16h4',
  },
  grip: {
    // Griff zum Ziehen: sechs Punkte in zwei Spalten
    primary:
      'M9 4.9a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM15 4.9a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM9 10.9a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM15 10.9a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM9 16.9a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM15 16.9a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2Z',
  },
  image: {
    primary:
      'M5 4.8h14a1.2 1.2 0 0 1 1.2 1.2v12a1.2 1.2 0 0 1-1.2 1.2H5A1.2 1.2 0 0 1 3.8 18V6A1.2 1.2 0 0 1 5 4.8Z',
    detail: 'M4 16.5l4.6-4.6 3.6 3.6 2.6-2.6 5.2 5.2M15.5 9.5h.01',
  },
  video: {
    primary:
      'M4.6 6.5h9.8a1.2 1.2 0 0 1 1.2 1.2v8.6a1.2 1.2 0 0 1-1.2 1.2H4.6a1.2 1.2 0 0 1-1.2-1.2V7.7a1.2 1.2 0 0 1 1.2-1.2Z',
    detail: 'M15.6 10.6 20.6 7.6v8.8l-5-3',
  },
  barcode: {
    primary:
      'M3.5 8V5.5a2 2 0 0 1 2-2H8M16 3.5h2.5a2 2 0 0 1 2 2V8M20.5 16v2.5a2 2 0 0 1-2 2H16M8 20.5H5.5a2 2 0 0 1-2-2V16',
    detail: 'M7.5 8v8M10.5 8v8M13 8v8M16.5 8v8',
  },
};

/** Eigenschaften eines Piktogramms. */
export interface AppIconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  readonly name: IconName;
  /** Kantenlänge in Pixeln, Standard 24. */
  readonly size?: number;
  /** Beschreibung für Screenreader. Ohne Angabe gilt das Symbol als rein dekorativ. */
  readonly title?: string;
}

/** Zeichnet ein Piktogramm als SVG. */
export function AppIcon({ name, size = 24, title, ...rest }: AppIconProps) {
  const icon = paths[name];
  const labelled = title !== undefined;

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE.primary}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={labelled ? 'img' : 'presentation'}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
      {...rest}
    >
      {labelled ? <title>{title}</title> : null}
      <path d={icon.primary} />
      {icon.detail === undefined ? null : <path d={icon.detail} strokeWidth={ICON_STROKE.detail} />}
    </svg>
  );
}
